import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { firestore } from "../firebase";
import {
  collection,
  getDocs,
  doc,
  runTransaction,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

/* ---------- Layout ---------- */

const Page = styled.main`
  min-height: 70vh;
  padding: 40px 18px;
  background: #f7fafc;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Hero = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 22px;
  margin-bottom: 18px;

  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 14px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(26px, 3vw, 44px);
  color: #111827;
  font-weight: 900;
`;

const Subtitle = styled.p`
  margin: 10px 0 0;
  color: #4b5563;
  line-height: 1.6;
`;

const BtnGhost = styled(Link)`
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-weight: 900;
  text-decoration: none;

  &:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }
`;

/* ---------- Category filter ---------- */

const FilterCard = styled.div`
  margin: 12px 0 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 12px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.05);
`;

const FilterRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 720px) {
    justify-content: flex-start;
  }
`;

const Chips = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Chip = styled.button`
  appearance: none;
  border-radius: 999px;
  padding: 10px 14px;
  font-weight: 950;
  cursor: pointer;
  border: 1px solid #e5e7eb;
  background: ${({ $active }) => ($active ? "#0f766e" : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#111827")};

  &:hover {
    background: ${({ $active }) => ($active ? "#0f766e" : "#f9fafb")};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

const FilterHint = styled.div`
  color: #6b7280;
  font-weight: 800;
  font-size: 12px;
`;

/* ---------- Grid ---------- */

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
`;

const Card = styled.article`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.06);
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 44px rgba(0, 0, 0, 0.1);
  }
`;

const ImageButton = styled.button`
  border: none;
  padding: 0;
  background: #000;
  width: 100%;
  cursor: zoom-in;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 220px;
  object-fit: cover;
  display: block;
`;

const Body = styled.div`
  padding: 14px 16px 16px;
`;

const NameRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: flex-start;
`;

const ProductName = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 900;
  color: #111827;
  line-height: 1.25;
`;

const Price = styled.div`
  font-weight: 900;
  color: #0f766e;
  background: rgba(15, 118, 110, 0.1);
  border: 1px solid rgba(15, 118, 110, 0.18);
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 13px;
  white-space: nowrap;
`;

const MetaRow = styled.div`
  margin-top: 10px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const StockPill = styled.div`
  display: inline-flex;
  padding: 7px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 12px;
  border: 1px solid #e5e7eb;
  color: #111827;
  background: #fff;
`;

const CatPill = styled.div`
  display: inline-flex;
  padding: 7px 10px;
  border-radius: 999px;
  font-weight: 950;
  font-size: 12px;
  border: 1px solid rgba(15, 118, 110, 0.18);
  color: #0f766e;
  background: rgba(15, 118, 110, 0.1);
`;

const Description = styled.p`
  margin-top: 10px;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.6;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const AddBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(15, 118, 110, 0.22);
  background: rgba(15, 118, 110, 0.1);
  color: #0f766e;
  font-weight: 950;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.02);
  }
`;

const DisabledBtn = styled(AddBtn)`
  opacity: 0.55;
  cursor: not-allowed;
  &:hover {
    transform: none;
    filter: none;
  }
`;

const InfoCard = styled.div`
  margin-top: 18px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 16px 18px;
  font-weight: 700;
  color: #111827;
`;

/* ---------- Image modal (smaller + fits screen) ---------- */

const ImgOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  background: rgba(0, 0, 0, 0.85);
  display: grid;
  place-items: center;
  padding: 14px;
`;

const ImgModal = styled.div`
  width: min(920px, 94vw);
  max-height: 92vh;
  border-radius: 18px;
  overflow: hidden;
  background: #000;
  display: grid;
  grid-template-rows: auto 1fr;
`;

const ImgTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: rgba(11, 18, 32, 0.9);
  color: #fff;
`;

const ImgTitle = styled.div`
  font-weight: 950;
  font-size: 14px;
  opacity: 0.95;
`;

const ImgClose = styled.button`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border-radius: 12px;
  padding: 8px 10px;
  cursor: pointer;
  font-weight: 950;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const BigImgWrap = styled.div`
  display: grid;
  place-items: center;
  padding: 8px;
  overflow: auto; /* om bilden är enorm */
`;

const BigImg = styled.img`
  max-width: 100%;
  max-height: calc(92vh - 56px);
  width: auto;
  height: auto;
  display: block;
  object-fit: contain;
`;

/* ---------- Cart drawer ---------- */

const CartFab = styled.button`
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 1200;

  appearance: none;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-weight: 950;
  border-radius: 999px;
  padding: 12px 14px;
  box-shadow: 0 16px 50px rgba(0, 0, 0, 0.18);
  cursor: pointer;

  &:hover {
    transform: translateY(-1px);
    background: #f9fafb;
  }
`;

const CartOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: rgba(0, 0, 0, 0.45);
`;

const CartDrawer = styled.div`
  position: fixed;
  right: 0;
  top: 0;
  height: 100%;
  width: min(420px, 92vw);
  background: #fff;
  z-index: 1350;
  border-left: 1px solid #e5e7eb;
  box-shadow: -20px 0 70px rgba(0, 0, 0, 0.25);

  display: flex;
  flex-direction: column;
`;

const CartHead = styled.div`
  padding: 14px 14px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  background: radial-gradient(
      900px 360px at 20% 0%,
      rgba(15, 118, 110, 0.12),
      transparent 55%
    ),
    radial-gradient(900px 360px at 90% 0%, rgba(195, 122, 71, 0.14), transparent 55%),
    #ffffff;
`;

const CartTitle = styled.div`
  font-weight: 950;
  color: #111827;
`;

const CartClose = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  border-radius: 12px;
  padding: 8px 10px;
  cursor: pointer;
  font-weight: 950;

  &:hover {
    background: #f9fafb;
  }
`;

const CartBody = styled.div`
  padding: 14px;
  overflow: auto;

  display: grid;
  gap: 10px;
`;

const CartItem = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 12px;
  display: grid;
  gap: 8px;
`;

const CartRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
`;

const CartName = styled.div`
  font-weight: 950;
  color: #111827;
`;

const CartSub = styled.div`
  color: #6b7280;
  font-weight: 800;
  font-size: 12px;
`;

const QtyRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const QtyBtn = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 12px;
  padding: 8px 10px;
  cursor: pointer;
  font-weight: 950;

  &:hover {
    background: #f9fafb;
  }
`;

const RemoveBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(233, 78, 27, 0.28);
  background: rgba(233, 78, 27, 0.08);
  color: #e94e1b;
  font-weight: 950;
  border-radius: 999px;
  padding: 8px 12px;
  cursor: pointer;

  &:hover {
    filter: brightness(1.05);
  }
`;

const CartFoot = styled.div`
  padding: 14px;
  border-top: 1px solid #e5e7eb;
  display: grid;
  gap: 10px;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: 950;
  color: #111827;
`;

const BuyBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(15, 118, 110, 0.22);
  background: #0f766e;
  color: #fff;
  font-weight: 950;
  border-radius: 999px;
  padding: 12px 14px;
  cursor: pointer;

  &:hover {
    filter: brightness(1.03);
    transform: translateY(-1px);
  }
`;

const Muted = styled.div`
  color: #6b7280;
  font-weight: 800;
  font-size: 12px;
  line-height: 1.45;
`;

/* ---------- Payment modal ---------- */

const PayOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1600;
  background: rgba(0, 0, 0, 0.72);
  display: grid;
  place-items: center;
  padding: 16px;
`;

const PayModal = styled.div`
  width: min(760px, 94vw);
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 22px;
  box-shadow: 0 22px 90px rgba(0, 0, 0, 0.45);
  overflow: hidden;
`;

const PayTop = styled.div`
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #e5e7eb;

  background: radial-gradient(
      900px 360px at 20% 0%,
      rgba(15, 118, 110, 0.12),
      transparent 55%
    ),
    radial-gradient(900px 360px at 90% 0%, rgba(195, 122, 71, 0.14), transparent 55%),
    #ffffff;
`;

const PayTitle = styled.div`
  font-weight: 950;
  color: #111827;
  font-size: 16px;
`;

const PayClose = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  border-radius: 12px;
  padding: 8px 10px;
  cursor: pointer;
  font-weight: 950;

  &:hover {
    background: #f9fafb;
  }
`;

const PayBody = styled.div`
  padding: 16px;
  display: grid;
  gap: 14px;
`;

const PayGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const PayCard = styled.div`
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 18px;
  padding: 14px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.06);
`;

const PayLabel = styled.div`
  color: #6b7280;
  font-weight: 900;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const PayValue = styled.div`
  margin-top: 6px;
  font-weight: 950;
  color: #111827;
  font-size: 16px;
`;

const PayMessageBox = styled.div`
  margin-top: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  background: #0b1220;
  color: #fff;
  padding: 12px;
`;

const PayMessageTitle = styled.div`
  font-weight: 950;
  opacity: 0.95;
`;

const PayMessageText = styled.pre`
  margin: 10px 0 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
    "Courier New", monospace;
  font-size: 13px;
  line-height: 1.5;
  opacity: 0.95;
`;

const PayActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const PayBtn = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-weight: 950;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }
`;

const PayPrimary = styled(PayBtn)`
  border-color: rgba(15, 118, 110, 0.22);
  background: #0f766e;
  color: #fff;

  &:hover {
    filter: brightness(1.03);
  }
`;

const PayDanger = styled(PayBtn)`
  border-color: rgba(233, 78, 27, 0.28);
  background: rgba(233, 78, 27, 0.08);
  color: #e94e1b;
`;

const PayNote = styled.div`
  color: #6b7280;
  font-weight: 800;
  font-size: 12px;
  line-height: 1.45;
`;

/* ---------- Helpers ---------- */

const SWISH_NUMBER = "1234467825"; // ✅ ditt nummer

const normalizeCat = (s) => String(s || "").trim();
const uniqCats = (arr) => {
  const m = new Map();
  for (const c of arr) {
    const v = normalizeCat(c);
    if (v) m.set(v.toLowerCase(), v);
  }
  return Array.from(m.values()).sort((a, b) => a.localeCompare(b, "sv"));
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ category filter
  const [activeCat, setActiveCat] = useState("Alla");

  // Image modal
  const [imgOpen, setImgOpen] = useState(false);
  const [imgSrc, setImgSrc] = useState("");
  const [imgTitle, setImgTitle] = useState("");

  // Cart
  const [cartOpen, setCartOpen] = useState(false);
  const [cart, setCart] = useState([]); // [{id,name,price,qty,imageUrl}]

  // Payment modal
  const [payOpen, setPayOpen] = useState(false);
  const [copied, setCopied] = useState(""); // "msg" | "num" | ""

  const [checkoutBusy, setCheckoutBusy] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const snap = await getDocs(collection(firestore, "products"));
        setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const categories = useMemo(() => {
    const cats = uniqCats(products.map((p) => p.category));
    return ["Alla", ...cats];
  }, [products]);

  const filteredProducts = useMemo(() => {
    if (activeCat === "Alla") return products;
    return products.filter(
      (p) => normalizeCat(p.category).toLowerCase() === activeCat.toLowerCase()
    );
  }, [products, activeCat]);

  const sorted = useMemo(() => {
    const copy = [...filteredProducts];
    copy.sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
    return copy;
  }, [filteredProducts]);

  const productById = useMemo(() => {
    const map = new Map();
    for (const p of products) map.set(p.id, p);
    return map;
  }, [products]);

  const cartCount = useMemo(
    () => cart.reduce((sum, it) => sum + (it.qty || 0), 0),
    [cart]
  );

  const cartTotal = useMemo(
    () => cart.reduce((sum, it) => sum + (Number(it.price) || 0) * (it.qty || 0), 0),
    [cart]
  );

  const swishMessage = useMemo(() => {
    const line = cart.map((it) => `${it.name} x${it.qty}`).join(", ");
    return `Hofors BGK köp: ${line}\nSumma: ${Math.round(cartTotal)} kr`;
  }, [cart, cartTotal]);

  const openImage = (src, title) => {
    setImgSrc(src);
    setImgTitle(title || "Bild");
    setImgOpen(true);
  };

  const addToCart = (p) => {
    const stock = Number(p.stock ?? 0);
    if (stock <= 0) return;

    setCart((prev) => {
      const existing = prev.find((x) => x.id === p.id);
      const currentQty = existing?.qty || 0;

      if (currentQty >= stock) return prev;

      if (!existing) {
        return [
          ...prev,
          {
            id: p.id,
            name: p.name || "Produkt",
            price: Number(p.price) || 0,
            imageUrl: p.imageUrl || "",
            qty: 1,
          },
        ];
      }
      return prev.map((x) => (x.id === p.id ? { ...x, qty: x.qty + 1 } : x));
    });

    setCartOpen(true);
  };

  const incQty = (id) => {
    setCart((prev) => {
      const p = productById.get(id);
      const stock = Number(p?.stock ?? 0);
      return prev.map((x) => {
        if (x.id !== id) return x;
        if (x.qty >= stock) return x;
        return { ...x, qty: x.qty + 1 };
      });
    });
  };

  const decQty = (id) => {
    setCart((prev) =>
      prev
        .map((x) => (x.id === id ? { ...x, qty: x.qty - 1 } : x))
        .filter((x) => x.qty > 0)
    );
  };

  const removeItem = (id) => setCart((prev) => prev.filter((x) => x.id !== id));

  const copyToClipboard = async (txt, what) => {
    try {
      await navigator.clipboard.writeText(txt);
      setCopied(what);
      setTimeout(() => setCopied(""), 1200);
    } catch {
      alert("Kunde inte kopiera automatiskt. Markera och kopiera manuellt.");
    }
  };

  const openPayment = () => {
    if (!cart.length) return;
    setPayOpen(true);
  };

  const confirmSwishAndCheckout = async () => {
    if (!cart.length) return;

    setCheckoutBusy(true);
    try {
      await runTransaction(firestore, async (tx) => {
        const productSnaps = await Promise.all(
          cart.map((it) => tx.get(doc(firestore, "products", it.id)))
        );

        productSnaps.forEach((snap, i) => {
          if (!snap.exists()) throw new Error(`Produkten finns inte längre: ${cart[i].name}`);
          const currentStock = Number(snap.data().stock ?? 0);
          const want = Number(cart[i].qty ?? 0);
          if (want <= 0) throw new Error("Ogiltigt antal i varukorg.");
          if (currentStock < want) {
            throw new Error(
              `För lite lager för "${cart[i].name}". Kvar: ${currentStock}, du försökte köpa: ${want}`
            );
          }
        });

        productSnaps.forEach((snap, i) => {
          const ref = snap.ref;
          const currentStock = Number(snap.data().stock ?? 0);
          const want = Number(cart[i].qty ?? 0);
          tx.update(ref, { stock: currentStock - want });
        });
      });

      const order = {
        createdAt: serverTimestamp(),
        status: "pending_swish",
        swishNumber: SWISH_NUMBER,
        total: Math.round(cartTotal),
        message: swishMessage,
        items: cart.map((it) => ({
          productId: it.id,
          name: it.name,
          price: Number(it.price) || 0,
          qty: Number(it.qty) || 0,
        })),
      };

      await addDoc(collection(firestore, "orders"), order);

      setCart([]);
      setPayOpen(false);
      setCartOpen(false);

      const snap = await getDocs(collection(firestore, "products"));
      setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));

      alert("Tack! Ordern är registrerad och lagret är uppdaterat.");
    } catch (e) {
      alert(e?.message || "Något gick fel vid köp.");
    } finally {
      setCheckoutBusy(false);
    }
  };

  return (
    <>
      <SEO title="Produkter" description="Produkter och merch från Hofors BGK." />

      <Page>
        <Container>
          <Hero>
            <div>
              <Title>Våra produkter</Title>
              <Subtitle>Klicka på bilden för större. Lägg i kundvagn och swisha.</Subtitle>
            </div>
            <BtnGhost to="/">Tillbaka</BtnGhost>
          </Hero>

          {/* ✅ Category filter UI */}
          {!loading && categories.length > 1 ? (
            <FilterCard>
              <FilterRow>
                <Chips aria-label="Filtrera produkter på kategori">
                  {categories.map((c) => (
                    <Chip key={c} type="button" $active={activeCat === c} onClick={() => setActiveCat(c)}>
                      {c}
                    </Chip>
                  ))}
                </Chips>
                <FilterHint>
                  Visar: <b>{activeCat}</b> ({sorted.length})
                </FilterHint>
              </FilterRow>
            </FilterCard>
          ) : null}

          {loading ? (
            <InfoCard>Laddar produkter…</InfoCard>
          ) : (
            <Grid>
              {sorted.map((p) => {
                const stock = Number(p.stock ?? 0);
                const inCart = cart.find((x) => x.id === p.id)?.qty || 0;
                const soldOut = stock <= 0;

                return (
                  <Card key={p.id}>
                    <ImageButton
                      onClick={() => openImage(p.imageUrl, p.name)}
                      aria-label={`Visa större bild av ${p.name}`}
                      type="button"
                    >
                      <ProductImage src={p.imageUrl} alt={p.name} />
                    </ImageButton>

                    <Body>
                      <NameRow>
                        <ProductName>{p.name}</ProductName>
                        <Price>{p.price} SEK</Price>
                      </NameRow>

                      <MetaRow>
                        <StockPill>
                          Lager:{" "}
                          <span style={{ marginLeft: 6, fontWeight: 950 }}>
                            {soldOut ? "Slut" : stock}
                          </span>
                          {inCart > 0 ? (
                            <span style={{ marginLeft: 10, color: "#6b7280" }}>
                              (i kundvagn: {inCart})
                            </span>
                          ) : null}
                        </StockPill>

                        {p.category ? <CatPill>{p.category}</CatPill> : null}
                      </MetaRow>

                      {p.description && <Description>{p.description}</Description>}

                      <ActionsRow>
                        {soldOut ? (
                          <DisabledBtn type="button" disabled>
                            Slut i lager
                          </DisabledBtn>
                        ) : (
                          <AddBtn type="button" onClick={() => addToCart(p)}>
                            Lägg i kundvagn
                          </AddBtn>
                        )}
                      </ActionsRow>
                    </Body>
                  </Card>
                );
              })}
            </Grid>
          )}

          <InfoCard>
            Vid köp: Swisha <b>{SWISH_NUMBER}</b> och skriv vad du köper (t.ex. Keps, Mugg, Mössa).
            Du kan kopiera meddelande i betalningsrutan.
          </InfoCard>
        </Container>
      </Page>

      {/* Floating cart button */}
      <CartFab type="button" onClick={() => setCartOpen(true)}>
        Kundvagn ({cartCount})
      </CartFab>

      {/* Cart drawer */}
      {cartOpen && (
        <>
          <CartOverlay onMouseDown={() => setCartOpen(false)} />
          <CartDrawer role="dialog" aria-modal="true" aria-label="Kundvagn">
            <CartHead>
              <CartTitle>Kundvagn</CartTitle>
              <CartClose type="button" onClick={() => setCartOpen(false)}>
                Stäng ✕
              </CartClose>
            </CartHead>

            <CartBody>
              {!cart.length ? (
                <Muted>Din kundvagn är tom.</Muted>
              ) : (
                cart.map((it) => {
                  const p = productById.get(it.id);
                  const stock = Number(p?.stock ?? 0);

                  return (
                    <CartItem key={it.id}>
                      <CartRow>
                        <div>
                          <CartName>{it.name}</CartName>
                          <CartSub>
                            {it.price} SEK • Lager: {stock}
                          </CartSub>
                        </div>
                        <RemoveBtn type="button" onClick={() => removeItem(it.id)}>
                          Ta bort
                        </RemoveBtn>
                      </CartRow>

                      <CartRow>
                        <QtyRow>
                          <QtyBtn type="button" onClick={() => decQty(it.id)}>
                            −
                          </QtyBtn>
                          <div style={{ fontWeight: 950 }}>{it.qty}</div>
                          <QtyBtn
                            type="button"
                            onClick={() => incQty(it.id)}
                            disabled={it.qty >= stock}
                            style={
                              it.qty >= stock ? { opacity: 0.55, cursor: "not-allowed" } : undefined
                            }
                          >
                            +
                          </QtyBtn>
                        </QtyRow>

                        <div style={{ fontWeight: 950 }}>
                          {Math.round((Number(it.price) || 0) * (it.qty || 0))} SEK
                        </div>
                      </CartRow>
                    </CartItem>
                  );
                })
              )}
            </CartBody>

            <CartFoot>
              <TotalRow>
                <span>Summa</span>
                <span>{Math.round(cartTotal)} SEK</span>
              </TotalRow>

              <BuyBtn
                type="button"
                onClick={openPayment}
                disabled={!cart.length}
                style={!cart.length ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
              >
                Köp (Swish)
              </BuyBtn>

              <Muted>
                Obs: Detta registrerar en order och minskar lagret när du bekräftar att du har swishat.
              </Muted>
            </CartFoot>
          </CartDrawer>
        </>
      )}

      {/* Payment modal */}
      {payOpen && (
        <PayOverlay
          role="dialog"
          aria-modal="true"
          aria-label="Betalning via Swish"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setPayOpen(false);
          }}
        >
          <PayModal onMouseDown={(e) => e.stopPropagation()}>
            <PayTop>
              <PayTitle>Betalning • Swish</PayTitle>
              <PayClose type="button" onClick={() => setPayOpen(false)}>
                Stäng ✕
              </PayClose>
            </PayTop>

            <PayBody>
              <PayGrid>
                <PayCard>
                  <PayLabel>Swishnummer</PayLabel>
                  <PayValue>{SWISH_NUMBER}</PayValue>
                </PayCard>

                <PayCard>
                  <PayLabel>Summa</PayLabel>
                  <PayValue>{Math.round(cartTotal)} kr</PayValue>
                </PayCard>
              </PayGrid>

              <PayMessageBox>
                <PayMessageTitle>Meddelande att klistra in i Swish</PayMessageTitle>
                <PayMessageText>{swishMessage}</PayMessageText>
              </PayMessageBox>

              <PayActions>
                <PayBtn type="button" onClick={() => copyToClipboard(SWISH_NUMBER, "num")}>
                  {copied === "num" ? "Kopierad ✓" : "Kopiera nummer"}
                </PayBtn>

                <PayPrimary type="button" onClick={() => copyToClipboard(swishMessage, "msg")}>
                  {copied === "msg" ? "Kopierad ✓" : "Kopiera meddelande"}
                </PayPrimary>

                <PayPrimary
                  type="button"
                  onClick={confirmSwishAndCheckout}
                  disabled={checkoutBusy}
                  style={checkoutBusy ? { opacity: 0.65, cursor: "wait" } : undefined}
                >
                  {checkoutBusy ? "Bearbetar…" : "Jag har swishat"}
                </PayPrimary>

                <PayDanger type="button" onClick={() => setPayOpen(false)} disabled={checkoutBusy}>
                  Avbryt
                </PayDanger>
              </PayActions>

              <PayNote>
                1) Öppna Swish och swisha till numret ovan. <br />
                2) Klistra in meddelandet så blir det tydligt vad köpet gäller. <br />
                3) Tryck “Jag har swishat” för att registrera order och uppdatera lagret.
              </PayNote>
            </PayBody>
          </PayModal>
        </PayOverlay>
      )}

      {/* Image modal */}
      {imgOpen && (
        <ImgOverlay
          role="dialog"
          aria-modal="true"
          aria-label="Bild"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setImgOpen(false);
          }}
        >
          <ImgModal onMouseDown={(e) => e.stopPropagation()}>
            <ImgTop>
              <ImgTitle>{imgTitle}</ImgTitle>
              <ImgClose type="button" onClick={() => setImgOpen(false)}>
                Stäng ✕
              </ImgClose>
            </ImgTop>

            <BigImgWrap>
              <BigImg src={imgSrc} alt={imgTitle} />
            </BigImgWrap>
          </ImgModal>
        </ImgOverlay>
      )}
    </>
  );
};

export default Products;




